import React, { useEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { colors } from "utils";

const TimerBarContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: ${props => props.width || "100%"};
`;

const TimerBarRow = styled.div`
  display: flex !important;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: ${props => props.hasIcon ? 'flex-start' : 'center'};
  flex-direction: row !important;
`;

const ProgressBarBackground = styled.div`
  width: 100%;
  height: ${props => props.height || "20px"};
  background-color: ${props => props.backgroundColor};
  border-radius: 100px;
  overflow: hidden;
  position: relative;
`;

const shrinkAnimation = keyframes`
  from {
    width: 100%;
  }
  to {
    width: 0;
  }
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background-color: white;
  border-radius: 100px;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  ${props => props.isRunning ? css`
    animation: ${shrinkAnimation} ${props.duration}s linear forwards;
    animation-play-state: running;
  ` : css`
    animation: none;
    animation-play-state: paused;
  `}
`;

const TimeText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.nightlyWoods950};
  font-family: "Roboto", system-ui;
`;

export const TimerBar = ({
  duration = 60,
  backgroundColor,
  autoStart = true,
  showTimeText = false,
  hideOnComplete = true,
  height = "12px",
  width = "100%",
  onComplete = () => {},
  onTick = () => {}
}) => {
  const [isRunning, setIsRunning] = useState(autoStart);
  const [isVisible, setIsVisible] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(duration);
  const tickIntervalRef = useRef(null);
  const startTimeRef = useRef(null);
  const completionTimeoutRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);
    setTimeRemaining(duration);
    if (autoStart) {
      setIsRunning(true);
    }
  }, [duration, autoStart]);

  useEffect(() => {
    if (autoStart && isVisible) {
      console.log('Starting timer for', duration, 'seconds');

      // Clear any existing timers
      if (completionTimeoutRef.current) {
        clearTimeout(completionTimeoutRef.current);
      }
      if (tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current);
      }

      // Record the start time for this timer
      startTimeRef.current = Date.now();

      completionTimeoutRef.current = setTimeout(() => {
        setIsRunning(false);
        setTimeRemaining(0);
        onComplete();

        if (hideOnComplete) {
          setIsVisible(false);
        }
      }, duration * 1000);

      if (onTick) {
        let secondsLeft = duration;
        tickIntervalRef.current = setInterval(() => {
          secondsLeft--;
          setTimeRemaining(secondsLeft);
          onTick(secondsLeft);
          if (secondsLeft <= 0) {
            clearInterval(tickIntervalRef.current);
          }
        }, 1000);
      }

      return () => {
        if (completionTimeoutRef.current) {
          clearTimeout(completionTimeoutRef.current);
        }
        if (tickIntervalRef.current) {
          clearInterval(tickIntervalRef.current);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]); // Only depend on duration to prevent re-running

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `${secs}s`;
  };

  if (!isVisible) {
    return null;
  }

  return (
    <TimerBarContainer width={width}>
      <TimerBarRow>
        <ProgressBarBackground backgroundColor={backgroundColor} height={height} style={{ flex: 1 }}>
          <ProgressBarFill
            isRunning={isRunning}
            duration={duration}
            key={duration} // Force re-render when duration changes to restart animation
          />
        </ProgressBarBackground>
      </TimerBarRow>
      {showTimeText && (
        <TimeText>
          {timeRemaining > 0 ? `${formatTime(timeRemaining)} remaining` : "Time's up!"}
        </TimeText>
      )}
    </TimerBarContainer>
  );
};
